<script setup>
import { ref, watch, onMounted, onUnmounted, defineAsyncComponent, nextTick } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { withTrailingSlash } from '@/utils/siteUrl'

const CmsUiToggle = import.meta.env.MODE === 'cms'
  ? defineAsyncComponent(() => import('@/cms/components/CmsUiToggle.vue'))
  : null

defineProps({
  navItems: {
    type: Array,
    default: () => []
    // { label, to } | { label, children: [{ label, to?, href?, external? }] }
  },
  cmsMode: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const isMobileMenuOpen = ref(false)
const openDropdown = ref(null)

// Boundary-aware match (same rule vue-router's own active-class uses) so a dropdown's
// trigger/panel highlight whenever the current route is one of its children, even
// when — unlike The Academy or About Us — those children don't share the parent's
// URL prefix (e.g. What We Do's children live at /our-approach, /our-work, etc.).
function isChildActive(child) {
  if (!child.to) return false
  return route.path === child.to || route.path.startsWith(child.to + '/')
}

function isDropdownActive(item) {
  if (item.to && (route.path === item.to || route.path.startsWith(item.to + '/'))) return true
  return (item.children || []).some(isChildActive)
}

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (!isMobileMenuOpen.value) openDropdown.value = null
}

function closeMenu() {
  isMobileMenuOpen.value = false
  openDropdown.value = null
}

function toggleDropdown(label) {
  openDropdown.value = openDropdown.value === label ? null : label
  if (openDropdown.value) {
    // move focus into the opened menu
    nextTick(() => focusFirstMenuItem(label))
  }
}

function handleClickOutside(e) {
  if (!e.target.closest('.dropdown')) openDropdown.value = null
}

function focusFirstMenuItem(label) {
  if (!label) return
  const id = 'menu-' + label.split(' ').join('-')
  const menu = document.getElementById(id)
  if (!menu) return
  const items = menu.querySelectorAll('a.link.item, [role="menuitem"]')
  if (items.length) (items[0]).focus()
}

function focusLastMenuItem(label) {
  if (!label) return
  const id = 'menu-' + label.split(' ').join('-')
  const menu = document.getElementById(id)
  if (!menu) return
  const items = menu.querySelectorAll('a.link.item, [role="menuitem"]')
  if (items.length) (items[items.length - 1]).focus()
}

function focusTrigger(label) {
  // find the trigger button by matching text content
  const triggers = document.querySelectorAll('.dropdown .trigger')
  for (const t of triggers) {
    if (t.textContent && t.textContent.trim().startsWith(label)) {
      t.focus()
      break
    }
  }
}

function onTriggerKeydown(e, label) {
  const key = e.key || e.code
  if (key === 'ArrowDown' || key === 'Enter' || key === ' ' || key === 'Spacebar') {
    e.preventDefault()
    if (openDropdown.value !== label) toggleDropdown(label)
    nextTick(() => focusFirstMenuItem(label))
  } else if (key === 'ArrowUp') {
    e.preventDefault()
    if (openDropdown.value !== label) toggleDropdown(label)
    nextTick(() => focusLastMenuItem(label))
  } else if (key === 'Escape') {
    e.preventDefault()
    closeMenu()
    focusTrigger(label)
  }
}

function onMenuKeydown(e, label) {
  const id = 'menu-' + label.split(' ').join('-')
  const menu = document.getElementById(id)
  if (!menu) return
  const items = Array.from(menu.querySelectorAll('a.link.item, [role="menuitem"]'))
  if (!items.length) return
  const active = document.activeElement
  const idx = items.indexOf(active)

  const key = e.key || e.code
  if (key === 'ArrowDown') {
    e.preventDefault()
    const next = (idx + 1) % items.length
    items[next].focus()
  } else if (key === 'ArrowUp') {
    e.preventDefault()
    const prev = (idx - 1 + items.length) % items.length
    items[prev].focus()
  } else if (key === 'Home') {
    e.preventDefault()
    items[0].focus()
  } else if (key === 'End') {
    e.preventDefault()
    items[items.length - 1].focus()
  } else if (key === 'Escape') {
    e.preventDefault()
    closeMenu()
    focusTrigger(label)
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.body.style.overflow = ''
})

// jk-37 brutal fix: the mobile nav panel had no scroll lock at all — the
// page behind it stayed fully scrollable (and, being `position: absolute`
// not `fixed`, the panel itself would drift out of view with it) while the
// menu was open. Simplest stopgap: lock body scroll for as long as the menu
// is open, covering every path that can open/close it (toggle button, a
// nav-link click via closeMenu, Escape) since they all funnel through this
// one ref.
watch(isMobileMenuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<template>
  <header>
    <!-- Desktop-only scrim that dims the page while a mega-menu is open (CSS hides it < sm).
         Sits below the header row (top: 100%) so the nav stays lit; clicking it closes the menu. -->
    <Transition name="scrim">
      <div v-if="openDropdown" class="menu-scrim" aria-hidden="true" @click="closeMenu" />
    </Transition>
    <div class="inner">

      <RouterLink class="logo" to="/" aria-label="Sylvia Rodger Institute — Home" @click="closeMenu">
        <img class="logo-light" src="/logos/Logo_ForLightBG_Colour_RGB.svg" alt="Sylvia Rodger Institute" />
        <img class="logo-dark" src="/logos/Logo_ForDarkBG_Colour_RGB.svg" alt="Sylvia Rodger Institute" />
      </RouterLink>

      <div class="right">
        <div class="utility">
          <template v-if="cmsMode">
            <RouterLink to="/cms-guide" class="editor">✏ Editor guide</RouterLink>
            <RouterLink to="/cms-status" class="editor">Status →</RouterLink>
            <RouterLink to="/component-gallery" class="editor">Components →</RouterLink>
            <CmsUiToggle />
          </template>
        </div>

        <div class="main">
          <button
            class="burger"
            :class="{ open: isMobileMenuOpen }"
            :aria-expanded="isMobileMenuOpen.toString()"
            aria-label="Toggle navigation"
            @click="toggleMobileMenu"
          >
            <span class="line" />
            <span class="line" />
            <span class="line" />
          </button>

          <nav :class="{ open: isMobileMenuOpen }" aria-label="Main navigation">
            <template v-for="item in navItems" :key="item.label">
              <RouterLink
                v-if="!item.children && item.to"
                :to="withTrailingSlash(item.to)"
                class="link"
                active-class="active"
                @click="closeMenu"
              >
                {{ item.label }}
              </RouterLink>

              <div
                v-else-if="item.children"
                class="dropdown"
                :class="{ open: openDropdown === item.label, active: isDropdownActive(item) }"
              >
                <button
                  class="link trigger"
                  :class="{ active: isDropdownActive(item) }"
                  @click="toggleDropdown(item.label)"
                  @keydown="onTriggerKeydown($event, item.label)"
                  :aria-expanded="(openDropdown === item.label).toString()"
                  aria-haspopup="true"
                  :aria-controls="'menu-' + item.label.split(' ').join('-')"
                >
                  {{ item.label }} <span class="arrow">▾</span>
                </button>
                <div :id="'menu-'+item.label.split(' ').join('-')" class="menu" :aria-hidden="(openDropdown !== item.label).toString()" @keydown="onMenuKeydown($event, item.label)">
                  <div v-if="item.summary" class="summary" role="presentation">
                    <p class="heading">{{ item.heading || item.label }}</p>
                    <p class="text">{{ item.summary }}</p>
                  </div>
                  <ul class="links" role="menu">
                    <li v-for="child in item.children" :key="child.label" role="none">
                      <a
                        v-if="child.external"
                        :href="child.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="link item"
                        role="menuitem"
                        @click="closeMenu"
                      >
                        <span class="label">{{ child.label }} <span class="external" aria-hidden="true">↗</span></span>
                        <span v-if="child.description" class="description">{{ child.description }}</span>
                      </a>
                      <RouterLink
                        v-else
                        :to="withTrailingSlash(child.to)"
                        class="link item"
                        :class="{ active: isChildActive(child) }"
                        role="menuitem"
                        @click="closeMenu"
                      >
                        <span class="label">{{ child.label }}</span>
                        <span v-if="child.description" class="description">{{ child.description }}</span>
                      </RouterLink>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </nav>
        </div>
      </div>

    </div>
  </header>
</template>

<style lang="scss" scoped>
header {
  position: relative;
  z-index: $z-header;   // lift header + its dropdown menus above page content (e.g. the hero)
  --_utility-height: 36px;
  --_nav-height: 64px;

  > .inner {
    display: flex;
    align-items: stretch;
    max-width: $container;
    margin: 0 auto;
    padding-inline: $gutter;
    padding-block: $space-3;
  }

  // Dims the page while a mega-menu is open. Anchored to the bottom of the header
  // (top: 100%) so the header row stays lit; the card (later in the DOM) paints over it.
  // Desktop only — the < sm mobile menu expands inline with no scrim.
  .menu-scrim {
    display: none;

    @include from-sm {
      display: block;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      height: 100vh;
      background: rgba($color-ink, 0.8);   // Figma scrim rgba(27,15,64,0.8) — $color-ink is the indigo
    }
  }

  // Softer, slightly-slower fade for the scrim as the menu opens/closes.
  // Longer than the base $dur (0.15s) since a full-page surface reads better gentle.
  .scrim-enter-active,
  .scrim-leave-active {
    transition: opacity 0.3s $ease;
  }
  .scrim-enter-from,
  .scrim-leave-to {
    opacity: 0;
  }

  .logo {
    display: flex;
    align-items: center;
    padding-right: $space-5;
    text-decoration: none;
    flex-shrink: 0;

    img {
      height: 48px;
      width: auto;
      @include from-sm { height: 64px; }
    }

    // Theme-driven swap (same rule as _tokens.scss's dark-theme-tokens):
    // explicit [data-theme] wins, System mode falls through to the OS media
    // query. No JS needed — mirrors how every other themed value here works.
    .logo-dark { display: none; }
  }

  .right {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .utility {
    height: var(--_utility-height);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $space-2;

    .editor {
      font-size: $font-size-cap;
      font-weight: 600;
      text-decoration: none;
      padding: $space-1 $space-4;
      border-radius: $radius;
      color: var(--color-danger);
      border: 1px solid var(--color-danger);
      @include transition(background, color, border-color);

      &:hover         { background: var(--color-danger); color: var(--color-on-primary); }
      &:focus-visible { @include focus-ring; }
    }

  }

  .main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    // jk-37 correction: nav leaves normal flow entirely in the tier below
    // (position: absolute), so without this, .main's own rendered height
    // collapses toward 0 (burger hidden, nav no longer contributing) —
    // shrinking .right's, and so header's own, box. Everything that
    // assumes "the header's actual height" (`.menu-scrim`'s `top: 100%`;
    // the page content that starts right after `header` in normal flow)
    // then shifts up into where nav is separately floating — which is
    // exactly why nav appeared stuck inside the scrim's dimmed area, and
    // the dropdown card appeared to overlap the page content below it.
    // Reserving the same height nav's own `top` offset already assumes it
    // has (`--_nav-height`) keeps header's box the size it's always been.
    @media (min-width: #{$bp-sm}) and (max-width: 919.98px) {
      min-height: var(--_nav-height);
    }
  }

  nav {
    display: flex;
    position: absolute;
    // offset by .inner's padding-block since nav is positioned relative to
    // `header`, not `.inner` — otherwise the mobile panel overlaps the nav row.
    top: calc(#{$space-3} + var(--_utility-height) + var(--_nav-height));
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    background: var(--color-bg);
    border-bottom: 1px solid var(--color-border);
    box-shadow: var(--shadow-2);
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    @include transition(max-height, opacity);

    // jk-37 brutal fix: was a flat `500px` cap with `overflow: hidden` — any
    // dropdown whose content ran taller than that (a longer child list, a
    // wrapped label, a short landscape viewport) got silently clipped with
    // no way to scroll to the rest. `70vh` + `overflow-y: auto` can't clip
    // unreachable content: it always fits the actual viewport and scrolls
    // internally if it doesn't.
    &.open { max-height: 70vh; overflow-y: auto; opacity: 1; }

    // jk-37: 640–919.98px ("the 640-920px menu is awkward") — the from-md-lg
    // treatment below squeezes nav inline beside the logo, which isn't
    // enough room at these widths for 5 items (some with dropdown labels).
    // Breaks nav out of `.main`'s inline slot onto its own row below the
    // header, right-anchored with the same gutter clearance the inline
    // treatment has, single-line, with the base mobile rule's own
    // background cascading through (see the media query below for why
    // left/right/background aren't re-declared here). `.dropdown`s still
    // open as the usual mega-menu card (gated on `from-sm`, unchanged).
    @media (min-width: #{$bp-sm}) and (max-width: 919.98px) {
      position: absolute;
      top: calc(#{$space-3} + var(--_utility-height) + var(--_nav-height));
      // No left/right override here (2nd correction) — the first attempt
      // set `left`/`right: $gutter` explicitly, which fought with `.menu`'s
      // own `right: 0` (anchored to *nav's* right edge) in confusing ways
      // as other fixes landed. Simpler: leave left/right unset so the base
      // mobile rule's `left: 0; right: 0` (above) cascades through
      // unchanged — nav spans the full header width — and instead right-
      // align the *content* within that box (`justify-content: flex-end` +
      // `margin-right: $gutter` on the last item, below), and give `.menu`
      // its own explicit `right: $gutter !important` (see `.menu` below)
      // so the dropdown card no longer depends on nav's box shape at all.
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;

      // Right-anchors the trigger row inside nav's (full-width) box with a
      // gutter's clearance from the true right edge — .link's own padding
      // already provides clearance from the *left* side of whichever item
      // ends up first, so only the last item needs an explicit margin.
      > a:last-child,
      div.dropdown:last-child {
        margin-right: $gutter;
      }

      flex-wrap: nowrap;
      // Less top space, more bottom space (direct correction) — was
      // symmetric $space-2 (8px) both sides.
      padding: 0 0 $space-4;
      // No background override (2nd correction) — "upping the z-index and
      // giving it a background helps": the base mobile rule's own
      // `background: var(--color-surface-warm)` (above) now cascades
      // through unchanged, instead of being blanked with `background: none`.
      border: none;
      box-shadow: none;
      max-height: none;
      // jk-37 correction: nav had no z-index, so once it left normal flow
      // (position: absolute, above) it painted wherever plain DOM/tree
      // order put it relative to `.menu-scrim` — which, combined with
      // header's collapsed height (see `.main`'s min-height above), left
      // it sitting inside the scrim's dimmed region instead of above it.
      // An explicit z-index (menu-scrim has none, i.e. auto/0) makes nav —
      // and everything inside it, including the mega-menu dropdown card —
      // paint above the scrim reliably, not just by DOM-order coincidence.
      z-index: 1;
      // jk-37 correction: `overflow-x: auto` with `overflow-y: visible` —
      // per spec, when one axis is `visible` and the other isn't, `visible`
      // computes to `auto` too. So this was making nav scroll/clip on *both*
      // axes, not just horizontally — and the mega-menu dropdown card
      // (`.menu`, anchored to nav and deliberately extending below nav's
      // own box) got clipped by that vertical `auto` instead of floating
      // freely over the page. `overflow: visible` — no scroll container at
      // all — so the dropdown is never touched; the tradeoff is that if
      // the row genuinely doesn't fit (5 items at the 640px end of this
      // range is tight), it now overflows past the *left* edge (the row is
      // right-anchored) rather than scrolling to it. Real fix (not done
      // here): a dedicated inner wrapper around just the trigger row,
      // scrollable on its own, decoupled from `nav` itself so `.menu` keeps
      // a clean, unclipped containing block.
      overflow: visible;
      opacity: 1;
      gap: $space-1;
    }

    @include from-md-lg {
      // `relative` makes the nav row the anchor for the mega-menu card, so every
      // dropdown opens in the same right-aligned spot (not under its own trigger).
      // Must clear the base mobile rule's absolute offsets (top/left/right) — under
      // `relative` they'd otherwise push the whole nav row down into the hero.
      position: relative;
      top: auto;
      left: auto;
      right: auto;
      flex-direction: row;
      align-items: center;
      padding: 0;
      background: none;
      border: none;
      box-shadow: none;
      max-height: none;
      overflow: visible;
      opacity: 1;
      gap: $space-1;
    }

    .link {
      display: block;
      padding: $space-3 $space-4;
      // Direct correction: the child items' own `.label` (below, inside
      // `.dropdown .menu .links`) was $font-size-base/16px bold — bigger
      // than this trigger/top-level text, reading backwards (a child
      // menu item more prominent than its own parent). Swapped the two
      // tokens rather than introducing a new size: this (the parent) now
      // takes the larger one, the child label takes the smaller.
      font-size: $font-size-base;
      color: var(--color-text);
      text-decoration: none;
      border-radius: $radius-sm;
      background: none;
      border: none;
      cursor: pointer;
      font-family: $font-sans;
      @include transition(background, color);

      &:hover  { background: var(--color-fill); }
      &.active { color: var(--color-primary); font-weight: 600; }
      &.trigger {
        display: flex;
        align-items: center;
        gap: $space-1;
        width: 100%;
        text-align: left;
        @include from-sm { width: auto; }
      }

      @include from-sm { padding: $space-2 $space-3; }
    }

    .dropdown {
      // No `position: relative` here — the card anchors to the nav row (see `nav`
      // above), so every dropdown opens in the same place rather than under its trigger.
      &.open .arrow { transform: rotate(180deg); }

      .arrow {
        font-size: 0.75rem;
        @include transition(transform);
      }

      &.open .menu {
        display: block;
        @include from-sm { display: grid; }
      }

      .menu {
        display: none;
        // jk-37 — 640-919.98px: the dropdown card now anchors its own
        // right edge directly to a gutter, decoupled from nav's own box
        // shape (nav spans the full header width in this range — see
        // `nav`'s media query above). `!important` because this rule and
        // `@include from-sm`'s `right: 0` below target the same selector
        // with equal specificity and are both active in this range —
        // without it, the later-in-source `from-sm` rule would win.
        @media (min-width: #{$bp-sm}) and (max-width: 919.98px) {
          right: $gutter !important;
        }

        @include from-sm {
          grid-template-columns: minmax(200px, 240px) 1fr;
          position: absolute;
          top: calc(100% + #{$space-6});   // gap below the nav row before the card
          right: 0;   // right-aligned to the nav / page gutter — same spot for every parent
          // 696px matches the Figma; caps to the viewport so it never overflows on narrow desktops.
          width: min(696px, calc(100vw - #{$gutter} * 2));
          border-radius: $radius-sm;
          box-shadow: var(--shadow-2);
          // The card is fully self-styled here (no global `.card` class): each panel
          // rounds its own outer corners (.summary / .links) so there's no card-level
          // border or overflow clip to reveal a hairline around the blue panel.
        }

        .summary {
          display: none;

          @include from-sm {
            display: block;
            padding: $space-4;
            // Bold deep-blue panel (DS border-decorative-dark #3141A7) with cream
            // text (Figma) — a fixed brand colour, so it reads the same in both themes.
            background: $color-border-decorative-dark;
            border-radius: $radius-sm 0 0 $radius-sm;   // rounds the card's left corners
            color: $color-card-cream;
            // Static panel (not a link) — no hover/focus states.

            .heading {
              display: block;
              margin: 0 0 $space-2;
              font-size: $font-size-lg;
              font-weight: 600;
              line-height: 1.2;
              color: $color-card-cream;
            }

            .text {
              margin: 0;
              font-size: $font-size-sm;
              color: $color-card-cream;
              line-height: var(--lh-sm);
            }
          }
        }

        .links {
          list-style: none;
          padding: 0;
          margin: 0;

          @include from-sm {
            display: flex;
            flex-direction: column;
            gap: $space-3;
            padding: $space-2 0;
            background: var(--color-surface-warm);
            border-radius: 0 $radius-sm $radius-sm 0;   // rounds the card's right corners
            overflow: hidden;   // clips the full-width item hover bars to the corner
          }
        }

        .link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: $space-2 $space-2 $space-2 $space-6;
          // Direct correction — "remove the left padding from the child
          // menu": these child items had a full $space-8 (64px) left inset,
          // reading as oddly indented against the parent trigger above
          // them. Landed at $space-6 (32px) at this size, not a full zero —
          // some inset still reads better than none against the panel edge.
          // from-sm below keeps its own uniform $space-4 (16px) on all sides
          // — a smaller inset at that width, no separate override needed.
          border-radius: 0;
          @include from-sm { padding: $space-2 $space-4; }

          .external { font-size: $font-size-cap; color: var(--color-text-muted); }

          &.item {
            flex-direction: column;
            align-items: flex-start;
            gap: $space-1;

            &.active .label { color: $color-primary; }

            .label {
              display: flex;
              align-items: center;
              gap: $space-1;
              // Direct correction — was $font-size-base/16px, bigger than
              // the parent trigger's own text (see `nav`'s `.link` rule
              // above) — swapped to the smaller token so the parent reads
              // as the more prominent one, as it should.
              font-size: $font-size-sm;
              font-weight: 600;
              line-height: 1.2;
              color: var(--color-ink);
            }

            .description {
              display: none;

              @include from-sm {
                display: block;
                font-size: $font-size-sm;
                color: var(--color-text);
                line-height: var(--lh-sm);
              }
            }
          }
        }
      }
    }
  }

  .burger {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 36px;
    height: 28px;
    padding: $space-1;
    background: transparent;
    border: none;
    cursor: pointer;

    .line {
      width: 100%;
      height: 2px;
      background: var(--color-ink);
      transform-origin: left center;
      @include transition(transform, opacity);
    }

    &.open .line {
      &:first-child  { transform: rotate(45deg); }
      &:nth-child(2) { opacity: 0; }
      &:last-child   { transform: rotate(-45deg); }
    }

    @include from-sm { display: none; }
  }
}

// Top-level (not nested under `header`) — [data-theme='dark'] lives on
// <html>, an ancestor of header, not a descendant, so this can't be written
// inside the `header { ... }` block above.
[data-theme='dark'] header .logo-light { display: none; }
[data-theme='dark'] header .logo-dark  { display: block; }

// PARKED FOR LAUNCH (2026-08-05) alongside _tokens.scss's equivalent block
// — see docs/features/jk-9-dark-mode.md's "Parked for launch" follow-up.
// Without this, the OS dark-mode setting alone swapped the logo even with
// the header toggle and _tokens.scss's media query both gone.
// @media (prefers-color-scheme: dark) {
//   :root:not([data-theme='light']):not([data-theme='dark']) {
//     header .logo-light { display: none; }
//     header .logo-dark  { display: block; }
//   }
// }
</style>
